import type {
  BackupV1,
  Profile,
  ProgressPatch,
  ProgressSnapshot,
} from '@/domain/progress.types';

export type Unsubscribe = () => void;

/**
 * The seam between the UI and wherever progress actually lives.
 *
 * Every method is async even though localStorage is synchronous. That is the
 * whole point: no component may assume a synchronous write, so a networked
 * adapter can replace this one without touching a single component.
 */
export interface StorageAdapter {
  readonly id: 'local' | 'cloud' | 'hybrid';
  readonly capabilities: {
    multiProfile: boolean;
    remote: boolean;
    /** Whether external changes are pushed through `subscribe`. */
    realtime: boolean;
  };

  /** Run migrations and ensure at least one profile exists. */
  init(): Promise<void>;

  listProfiles(): Promise<Profile[]>;
  createProfile(name: string): Promise<Profile>;
  renameProfile(id: string, name: string): Promise<void>;
  deleteProfile(id: string): Promise<void>;
  /** Deep-copies progress into a new profile — "same checklist, second character". */
  duplicateProfile(id: string, name: string): Promise<Profile>;

  getProgress(profileId: string): Promise<ProgressSnapshot>;
  applyPatches(profileId: string, patches: ProgressPatch[]): Promise<ProgressSnapshot>;
  clearSet(profileId: string, setId: string): Promise<ProgressSnapshot>;
  /** Drops entries for items no longer present in content. */
  pruneOrphans(profileId: string, liveIdsBySet: Record<string, string[]>): Promise<ProgressSnapshot>;

  subscribe(profileId: string, cb: (snapshot: ProgressSnapshot) => void): Unsubscribe;

  exportBackup(): Promise<BackupV1>;
  importBackup(backup: BackupV1, mode: 'replace' | 'merge'): Promise<void>;
}

/* ------------------------------------------------------------------ *
 * Shared pure helpers — used by every adapter, and unit-tested directly.
 * ------------------------------------------------------------------ */

export function applyPatchesTo(
  snapshot: ProgressSnapshot,
  patches: readonly ProgressPatch[],
  now: number = Date.now(),
): ProgressSnapshot {
  if (patches.length === 0) return snapshot;
  const sets: ProgressSnapshot['sets'] = { ...snapshot.sets };

  for (const patch of patches) {
    const current = { ...(sets[patch.setId] ?? {}) };
    if (patch.entry === null) {
      delete current[patch.itemId];
    } else {
      const existing = current[patch.itemId];
      current[patch.itemId] = {
        done: patch.entry.done ?? existing?.done ?? false,
        ...(patch.entry.note !== undefined
          ? { note: patch.entry.note }
          : existing?.note !== undefined
            ? { note: existing.note }
            : {}),
        updatedAt: now,
      };
    }
    sets[patch.setId] = current;
  }

  return { ...snapshot, sets, rev: snapshot.rev + 1, updatedAt: now };
}

/**
 * Last-writer-wins merge at ENTRY granularity.
 *
 * Used by import(merge) today and by cloud sync later. Because every entry
 * carries its own `updatedAt`, two devices that ticked different items merge
 * cleanly instead of one clobbering the other.
 */
export function mergeSnapshots(
  base: ProgressSnapshot,
  incoming: ProgressSnapshot,
): ProgressSnapshot {
  const sets: ProgressSnapshot['sets'] = { ...base.sets };

  for (const [setId, incomingSet] of Object.entries(incoming.sets)) {
    const merged = { ...(sets[setId] ?? {}) };
    for (const [itemId, incomingEntry] of Object.entries(incomingSet)) {
      const mine = merged[itemId];
      if (!mine || incomingEntry.updatedAt > mine.updatedAt) merged[itemId] = incomingEntry;
    }
    sets[setId] = merged;
  }

  return {
    ...base,
    sets,
    rev: Math.max(base.rev, incoming.rev) + 1,
    updatedAt: Date.now(),
  };
}

/** Rewrites historical item ids onto their current ids. Idempotent. */
export function remapAliases(
  snapshot: ProgressSnapshot,
  aliasesBySet: Record<string, ReadonlyMap<string, string>>,
): ProgressSnapshot {
  let changed = false;
  const sets: ProgressSnapshot['sets'] = { ...snapshot.sets };

  for (const [setId, aliases] of Object.entries(aliasesBySet)) {
    const setProgress = sets[setId];
    if (!setProgress || aliases.size === 0) continue;
    const next = { ...setProgress };
    for (const [oldId, newId] of aliases) {
      const entry = next[oldId];
      if (!entry) continue;
      const existing = next[newId];
      // The live id wins only if it was touched more recently.
      if (!existing || entry.updatedAt > existing.updatedAt) next[newId] = entry;
      delete next[oldId];
      changed = true;
    }
    sets[setId] = next;
  }

  return changed ? { ...snapshot, sets } : snapshot;
}

export function countDone(
  snapshot: ProgressSnapshot,
  setId: string,
  itemIds: readonly string[],
): number {
  const set = snapshot.sets[setId];
  if (!set) return 0;
  let n = 0;
  for (const id of itemIds) if (set[id]?.done) n += 1;
  return n;
}
